/* eslint-disable @typescript-eslint/no-explicit-any */
import { toNestError, validateFieldsNatively } from "@hookform/resolvers";
import Ajv, { DefinedError } from "ajv";
import ajvErrors from "ajv-errors";
import { appendErrors, FieldError, ValidateResult } from "react-hook-form";
import { Resolver } from "./types";
import {
  maxStartingRepeatedCharsValidation,
  validateAddressField,
  validateCapField,
  validateDateField,
  validateDatesComparison,
  validateDatesExclusion,
  validateOtpField,
  validateTaxCodeField,
  passwordFieldValidator,
} from "./validators";

const parseErrorSchema = (
  ajvDefinedErrors: DefinedError[] = [],
  validateAllFieldCriteria = true,
  values?: any,
) => {
  ajvDefinedErrors.forEach(error => {
    if (error.keyword === "required") {
      error.instancePath += "/" + error.params.missingProperty;
    }
  });

  return ajvDefinedErrors.reduce<Record<string, FieldError>>(
    (previous, error) => {
      const path = error.instancePath.substring(1).replace(/\//g, ".");
      // add ability to parse error message with the placeholders for string substitution.
      // E.g. #{fieldName} will be replaced with the value of fieldName.
      const matchRegex = /#{(.+)}/gim;
      const match = error.message?.match(matchRegex);
      if (match) {
        match.forEach(m => {
          let strippedFieldName = m.replaceAll(/#{/g, "");
          strippedFieldName = strippedFieldName.replaceAll(/}/g, "");
          const fieldValue = values[strippedFieldName];
          if (fieldValue) {
            error.message = error.message?.replace(m, fieldValue);
          }
        });
      }

      if (!previous[path]) {
        previous[path] = {
          message: error.message,
          type: error.keyword,
        };
      }

      if (validateAllFieldCriteria) {
        const types = previous[path].types;
        const messages = types && types[error.keyword];

        previous[path] = appendErrors(
          path,
          validateAllFieldCriteria,
          previous,
          error.keyword,
          messages
            ? (([] as string[]).concat(
                messages as unknown as string | ConcatArray<string>,
                error.message || "",
              ) as unknown as ValidateResult)
            : error.message,
        ) as FieldError;
      }

      return previous;
    },
    {},
  );
};

export const customAjvResolver: Resolver =
  (
    schema,
    schemaOptions,
    resolverOptions = {},
    onValidated = () => null,
    onInvalid = () => null,
    onValidationStarted = () => null,
  ) =>
  async (values, _, options) => {
    const validatingFields = options.names as string[];
    const ajv = new Ajv({
      allErrors: true,
      validateSchema: true,
      ...schemaOptions,
    });
    // custom taxField validator.
    ajv.addKeyword({ keyword: "firstNameField", type: "string" });
    ajv.addKeyword({ keyword: "lastNameField", type: "string" });
    ajv.addKeyword({
      validate: (...args: any) =>
        validateTaxCodeField({
          args,
          names: validatingFields,
          onValidated,
          onInvalid,
          onValidationStarted,
        }),
      keyword: "taxCode",
      type: "string",
      async: true,
    });

    // custom cap validator.
    ajv.addKeyword({ keyword: "capProvinceFieldName", type: "string" });
    ajv.addKeyword({ keyword: "capCityFieldName", type: "string" });
    ajv.addKeyword({
      validate: (...args: any) =>
        validateCapField({
          args,
          names: validatingFields,
          onValidated,
          onInvalid,
          onValidationStarted,
        }),
      type: "string",
      keyword: "cap",
      async: true,
    });

    // custom address validator.
    ajv.addKeyword({
      validate: (...args: any) =>
        validateAddressField({
          args,
          names: validatingFields,
          onValidated,
          onInvalid,
          onValidationStarted,
        }),
      type: "string",
      keyword: "address",
      async: true,
    });

    // max starting repeated chars validator
    ajv.addKeyword({
      validate: (...args: any) =>
        maxStartingRepeatedCharsValidation({
          args,
          names: validatingFields,
          onValidated,
          onInvalid,
          onValidationStarted,
        }),
      type: "string",
      keyword: "maxStartingRepeatedChars",
      async: true,
    });

    // date validator
    ajv.addKeyword({ keyword: "minDate", type: "string" });
    ajv.addKeyword({ keyword: "maxDate", type: "string" });
    ajv.addKeyword({ keyword: "dateFormat", type: "string" });
    ajv.addKeyword({
      validate: (...args: any) =>
        validateDateField({
          args,
          names: validatingFields,
          onValidated,
          onInvalid,
          onValidationStarted,
        }),
      keyword: "date",
      type: "string",
      async: true,
    });

    // exclude dates
    ajv.addKeyword({
      validate: (...args: any) =>
        validateDatesExclusion({
          args,
          names: validatingFields,
          onValidated,
          onInvalid,
          onValidationStarted,
        }),
      keyword: "datesToExclude",
      type: "string",
      async: true,
    });

    // date comparisons object
    ajv.addKeyword({
      validate: (...args: any) =>
        validateDatesComparison({
          args,
          names: validatingFields,
          onValidated,
          onInvalid,
          onValidationStarted,
        }),
      keyword: "datesComparison",
      type: "string",
      async: true,
    });

    // otp validator
    ajv.addKeyword({ keyword: "otpLength", type: "string" });
    ajv.addKeyword({
      validate: (...args: any) =>
        validateOtpField({
          args,
          names: validatingFields,
          onValidated,
          onInvalid,
          onValidationStarted,
        }),
      keyword: "otp",
      async: true,
      type: "string",
    });

    ajv.addKeyword({
      validate: (...args: any) =>
        passwordFieldValidator({
          args,
          names: validatingFields,
          onValidated,
          onInvalid,
          onValidationStarted,
        }),
      keyword: "passwordValidator",
      async: true,
      type: "string",
    });

    ajvErrors(ajv);

    const validate: Awaited<any> = ajv.compile(
      Object.assign({ $async: resolverOptions?.mode === "async" }, schema),
    );
    try {
      const validateValues = await validate(values);
      if (options.shouldUseNativeValidation) {
        validateFieldsNatively({}, options);
      }

      return {
        values: validateValues,
        errors: {},
      };
    } catch (e) {
      const dataToRet = {
        values: {},
        errors: toNestError(
          parseErrorSchema(
            (e as Record<string, unknown>).errors as DefinedError[],
            !options.shouldUseNativeValidation &&
              options.criteriaMode === "all",
            values,
          ),
          options,
        ),
      };
      return dataToRet;
    }
  };
